import { useState, useEffect, useContext, useRef } from "react";
import api from "../api/api";
import { formatearFecha } from "../utils/fechas";
import { AuthContext } from "../context/AuthContext";
import ArgentinaPhoneInput from "../components/ArgentinaPhoneInput";
import ReportarProblema from "../components/ReportarProblema";

// ─── Constantes ───────────────────────────────────────────────────────────────
const PROVINCIAS = [
  "Buenos Aires",
  "CABA",
  "Catamarca",
  "Chaco",
  "Chubut",
  "Córdoba",
  "Corrientes",
  "Entre Ríos",
  "Formosa",
  "Jujuy",
  "La Pampa",
  "La Rioja",
  "Mendoza",
  "Misiones",
  "Neuquén",
  "Río Negro",
  "Salta",
  "San Juan",
  "San Luis",
  "Santa Cruz",
  "Santa Fe",
  "Santiago del Estero",
  "Tierra del Fuego",
  "Tucumán",
];

const STATUS_CONFIG = {
  active:            { label: "Activo",           color: "#1b7f4e", bg: "#e8f7ef" },
  pending_review:    { label: "En revisión",       color: "#b06a00", bg: "#fff3e0" },
  pending_documents: { label: "Docs. pendientes",  color: "#b06a00", bg: "#fff3e0" },
  draft:             { label: "Borrador",          color: "#5c7a8a", bg: "#f0f5f8" },
  suspended:         { label: "Suspendido",        color: "#c0392b", bg: "#fdecea" },
  rejected:          { label: "Rechazado",         color: "#c0392b", bg: "#fdecea" },
  approved:          { label: "Aprobado",          color: "#1b7f4e", bg: "#e8f7ef" },
  expired:           { label: "Vencido",           color: "#7b5ea7", bg: "#f3eeff" },
};

const SERVICE_ICONS = {
  "Plomería": "🔧", "Gasista": "🔥", "Electricidad": "⚡", "Pintura": "🎨",
  "Albañilería": "🧱", "Cerrajería": "🔑", "Limpieza": "🧹", "Jardinería": "🌿",
};

const DIAS_LABELS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const font = "Montserrat, sans-serif";

// ─── Formateadores DNI y CUIT/CUIL ───────────────────────────────────────────
function formatDNI(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 5) return d.slice(0, d.length - 3) + "." + d.slice(-3);
  return d.slice(0, d.length - 6) + "." + d.slice(d.length - 6, d.length - 3) + "." + d.slice(-3);
}

function formatCUIT(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2)  return d;
  if (d.length <= 10) return d.slice(0, 2) + "-" + d.slice(2);
  return d.slice(0, 2) + "-" + d.slice(2, 10) + "-" + d.slice(10);
}

function onlyDigits(val) { return val.replace(/\D/g, ""); }

// ─── Hook geolocalización ─────────────────────────────────────────────────────
function useGeoDetect() {
  const [detecting, setDetecting] = useState(false);
  const [geoError,  setGeoError]  = useState("");
  const detect = (onResult) => {
    setGeoError("");
    if (!navigator.geolocation) { setGeoError("Tu navegador no soporta geolocalización."); return; }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${coords.latitude}&lon=${coords.longitude}&format=json&addressdetails=1`, { headers: { "Accept-Language": "es" } });
          const data = await res.json();
          const a = data.address || {};
          onResult({ street: [a.road, a.house_number].filter(Boolean).join(" ") || "", city: a.city || a.town || a.village || a.municipality || "", province: a.state || "", postal_code: a.postcode || "" });
        } catch { setGeoError("No se pudo obtener la dirección."); }
        finally { setDetecting(false); }
      },
      (err) => { setDetecting(false); setGeoError(err.code === 1 ? "Permiso de ubicación denegado." : "No se pudo obtener la ubicación."); },
      { timeout: 8000, maximumAge: 60000 }
    );
  };
  return { detect, detecting, geoError, clearGeoError: () => setGeoError("") };
}

// ─── Shared styles ────────────────────────────────────────────────────────────
const inputStyle = {
  padding: "10px 14px", borderRadius: 9, border: "1.5px solid rgba(0,0,0,0.12)",
  fontSize: 14, fontFamily: font, color: "#163244",
  background: "#fff", outline: "none", width: "100%", boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const labelStyle = {
  display: "block", fontSize: 10, fontWeight: 700, color: "#7a93a4",
  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
};

// ─── Shared components ────────────────────────────────────────────────────────
function Badge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#5c7a8a", bg: "#f0f5f8" };
  return (
    <span style={{
      padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      letterSpacing: "0.04em", color: cfg.color, background: cfg.bg, display: "inline-block",
    }}>
      {cfg.label.toUpperCase()}
    </span>
  );
}

function SectionCard({ title, subtitle, action, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 16, border: "1px solid rgba(0,0,0,0.07)", marginBottom: 18, overflow: "hidden" }}>
      <div style={{ padding: "16px 24px", borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#163244" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: "#7a93a4", marginTop: 2 }}>{subtitle}</div>}
        </div>
        {action}
      </div>
      <div style={{ padding: "22px 24px" }}>{children}</div>
    </div>
  );
}

function SaveButton({ onClick, loading, saved }) {
  return (
    <button onClick={onClick} style={{
      padding: "8px 20px", borderRadius: 50,
      background: saved ? "#1b7f4e" : "#163244",
      color: "#fff", border: "none", fontFamily: font,
      fontWeight: 700, fontSize: 12, cursor: "pointer",
      display: "flex", alignItems: "center", gap: 6, transition: "background 0.3s",
    }}>
      {loading
        ? <span style={{ width: 12, height: 12, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
        : saved ? "✓ Guardado" : "Guardar"
      }
    </button>
  );
}

function calcHours(start, end) {
  try {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    const diff = (eh * 60 + em) - (sh * 60 + sm);
    return diff > 0 ? (diff / 60).toFixed(1) : "—";
  } catch { return "—"; }
}

function ErrorMsg({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ background: "rgba(192,57,43,0.07)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 9, padding: "9px 14px", fontSize: 13, color: "#c0392b", fontWeight: 600, marginTop: 12 }}>
      ⚠ {msg}
    </div>
  );
}

// ─── Sección foto de perfil ───────────────────────────────────────────────────
function compressImage(file, maxPx = 800, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        if (width > height) { height = Math.round(height * maxPx / width); width = maxPx; }
        else { width = Math.round(width * maxPx / height); height = maxPx; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = url;
  });
}

function PhotoSection({ currentPhoto, onUpdate }) {
  const inputRef              = useRef(null);
  const [preview,      setPreview]      = useState(currentPhoto || "");
  const [loading,      setLoading]      = useState(false);
  const [compressing,  setCompressing]  = useState(false);
  const [saved,        setSaved]        = useState(false);
  const [error,        setError]        = useState("");
  const [dragging,     setDragging]     = useState(false);

  const processFile = async (file) => {
    setError(""); setSaved(false);
    if (!file) return;
    if (!["image/jpeg","image/png","image/webp"].includes(file.type)) {
      setError("Usá JPG, PNG o WebP."); return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("La imagen supera 10 MB."); return;
    }
    setCompressing(true);
    try {
      const compressed = await compressImage(file);
      setPreview(compressed);
    } catch {
      setError("No se pudo procesar la imagen. Intentá con otra.");
    } finally {
      setCompressing(false);
    }
  };

  const handleSave = async () => {
    if (!preview || preview === currentPhoto) return;
    setLoading(true); setError("");
    try {
      await api.post("/users/me/photo", { photo: preview });
      onUpdate(preview);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar la foto.");
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    setLoading(true); setError("");
    try {
      await api.delete("/users/me/photo");
      setPreview(""); onUpdate("");
    } catch (err) {
      setError("Error al eliminar la foto.");
    } finally { setLoading(false); }
  };

  const changed = preview && preview !== currentPhoto;

  return (
    <SectionCard
      title="Foto de perfil"
      subtitle="Requerida para verificación de identidad"
      action={
        changed ? (
          <button onClick={handleSave} disabled={loading} style={{ padding: "8px 20px", borderRadius: 50, background: saved ? "#1b7f4e" : "#163244", color: "#fff", border: "none", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            {loading
              ? <span style={{ width: 12, height: 12, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
              : saved ? "✓ Guardado" : "Guardar foto"
            }
          </button>
        ) : null
      }
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>

        {/* Vista previa */}
        <div style={{ flexShrink: 0 }}>
          {preview ? (
            <img src={preview} alt="Foto de perfil" style={{ width: 100, height: 100, borderRadius: "50%", objectFit: "cover", border: "3px solid #163244" }} />
          ) : (
            <div style={{ width: 100, height: 100, borderRadius: "50%", background: "#163244", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, color: "#E7A432" }}>
              📷
            </div>
          )}
        </div>

        {/* Upload zone */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); processFile(e.dataTransfer.files?.[0]); }}
            style={{
              border: `2px dashed ${dragging ? "#163244" : "rgba(0,0,0,0.13)"}`,
              borderRadius: 12, padding: "18px 16px", cursor: "pointer", textAlign: "center",
              background: dragging ? "rgba(22,50,68,0.04)" : "#fafafa", transition: "all 0.2s",
              marginBottom: 10,
            }}
          >
            <div style={{ fontSize: 22, marginBottom: 6 }}>{compressing ? "⏳" : "📁"}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#163244", marginBottom: 2 }}>{compressing ? "Procesando imagen…" : "Subir nueva foto"}</div>
            <div style={{ fontSize: 11, color: "#7a93a4" }}>JPG, PNG o WebP · Máx. 10 MB · Se comprime automáticamente</div>
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={e => processFile(e.target.files?.[0])} />
          </div>

          {/* Info biométrica */}
          <div style={{ background: "rgba(231,164,50,0.08)", border: "1px solid rgba(231,164,50,0.25)", borderRadius: 9, padding: "10px 14px", fontSize: 12, color: "#b8820d", lineHeight: 1.55 }}>
            🔐 <strong>¿Por qué necesitamos tu foto?</strong><br/>
            Tu imagen será utilizada para futuros controles de identidad biométrica, garantizando la seguridad de clientes y prestadores en Fixo.
          </div>

          {error && <div style={{ fontSize: 12, color: "#c0392b", fontWeight: 600, marginTop: 8 }}>⚠ {error}</div>}

          {preview && (
            <button onClick={handleDelete} disabled={loading} style={{ marginTop: 10, background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#c0392b", fontWeight: 600, fontFamily: font, padding: 0 }}>
              🗑 Eliminar foto
            </button>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

// ─── Sección perfil ───────────────────────────────────────────────────────────
function ProfileSection({ data, onUpdate }) {
  const [form,    setForm]    = useState({ ...data });
  const [loading, setLoading] = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");

  const update = (field, val) => { setForm(f => ({ ...f, [field]: val })); setSaved(false); };

  const handleSave = async () => {
    setLoading(true); setError("");
    try {
      await api.put("/providers/me", {
        display_name: form.display_name,
        legal_name:   form.legal_name,
        dni:          onlyDigits(form.dni  || ""),
        cuit:         onlyDigits(form.cuit || ""),
        phone:        form.phone || undefined,
        bio:          form.bio,
      });
      setSaved(true);
      onUpdate(form);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar. Intentá de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    // El subtítulo dice qué ve el cliente, y tiene que ser cierto (29-10-2026):
    // nombre y foto en los presupuestos; desde que acepta, nombre y apellido en
    // el chat (users.name), teléfono y email.
    // Nombre legal, DNI y CUIT no le llegan nunca. El mismo texto está en la
    // app ((prestador)/perfil.tsx).
    <SectionCard title="Datos del perfil" subtitle="Los clientes ven tu nombre y tu foto en tus presupuestos, y tu nombre y apellido, tu teléfono y tu email cuando aceptan uno. El nombre legal, el DNI y el CUIT son de uso interno." action={<SaveButton onClick={handleSave} loading={loading} saved={saved} />}>
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Nombre para mostrar *</label>
          <input style={inputStyle} value={form.display_name} onChange={e => update("display_name", e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Nombre legal</label>
          <input style={inputStyle} value={form.legal_name || ""} onChange={e => update("legal_name", e.target.value)} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>DNI</label>
          <input
            style={{ ...inputStyle, borderColor: form.dni && onlyDigits(form.dni).length !== 8 ? "#E7A432" : "rgba(0,0,0,0.12)" }}
            value={formatDNI(form.dni)}
            onChange={e => update("dni", onlyDigits(e.target.value).slice(0, 8))}
            placeholder="XX.XXX.XXX"
            inputMode="numeric"
          />
          {form.dni && onlyDigits(form.dni).length !== 8 && (
            <div style={{ fontSize: 10, color: "#b06a00", marginTop: 4 }}>El DNI debe tener 8 dígitos ({onlyDigits(form.dni).length}/8)</div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>CUIT / CUIL</label>
          <input
            style={{ ...inputStyle, borderColor: form.cuit && onlyDigits(form.cuit).length !== 11 ? "#E7A432" : "rgba(0,0,0,0.12)" }}
            value={formatCUIT(form.cuit)}
            onChange={e => update("cuit", onlyDigits(e.target.value).slice(0, 11))}
            placeholder="XX-XXXXXXXX-X"
            inputMode="numeric"
          />
          {form.cuit && onlyDigits(form.cuit).length !== 11 && (
            <div style={{ fontSize: 10, color: "#b06a00", marginTop: 4 }}>El CUIT debe tener 11 dígitos ({onlyDigits(form.cuit).length}/11)</div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Teléfono</label>
          <ArgentinaPhoneInput
            value={form.phone || ""}
            onChange={phone => update("phone", phone)}
          />
        </div>
      </div>
      <div>
        <label style={labelStyle}>Presentación</label>
        <textarea style={{ ...inputStyle, resize: "vertical", minHeight: 90, lineHeight: 1.6 }} value={form.bio || ""} onChange={e => update("bio", e.target.value)} maxLength={500} />
        <div style={{ textAlign: "right", fontSize: 11, color: "#8fa3b1", marginTop: 2 }}>{(form.bio || "").length}/500</div>
      </div>
      <ErrorMsg msg={error} />
    </SectionCard>
  );
}

// ─── Sección datos personales (users/me) ──────────────────────────────────────
function PersonalDataSection() {
  const { setUser } = useContext(AuthContext);

  const [form,    setForm]    = useState({ first_name: "", last_name: "", username: "", phone: "", street: "", city: "", province: "", postal_code: "" });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");
  const { detect, detecting, geoError, clearGeoError } = useGeoDetect();

  useEffect(() => {
    api.get("/users/me")
      .then(res => {
        const u = res.data.user || res.data;
        setForm({
          first_name:  u.first_name  || "",
          last_name:   u.last_name   || "",
          username:    u.username    || "",
          phone:       u.phone       || "",
          street:      u.street      || "",
          city:        u.city        || "",
          province:    u.province    || "",
          postal_code: u.postal_code || "",
        });
      })
      .catch(() => setError("No se pudo cargar los datos personales."))
      .finally(() => setLoading(false));
  }, []);

  const update = (field, val) => { setForm(f => ({ ...f, [field]: val })); setSaved(false); };

  const handleSave = async () => {
    setError("");
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError("Nombre y apellido son obligatorios.");
      return;
    }
    if (form.username && !/^[a-zA-Z0-9_]{3,20}$/.test(form.username)) {
      setError("El nombre de usuario debe tener entre 3 y 20 caracteres (letras, números y _).");
      return;
    }
    setSaving(true);
    try {
      const res = await api.put("/users/me", {
        first_name:  form.first_name.trim(),
        last_name:   form.last_name.trim(),
        username:    form.username.trim()     || undefined,
        phone:       form.phone              || undefined,
        street:      form.street.trim()      || undefined,
        city:        form.city.trim()        || undefined,
        province:    form.province.trim()    || undefined,
        postal_code: form.postal_code.trim() || undefined,
      });
      // Refrescar el contexto global
      if (setUser) {
        const meRes = await api.get("/users/me");
        setUser(meRes.data.user || meRes.data);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      if (err.response?.status === 409) {
        setError("El nombre de usuario ya está en uso. Elegí otro.");
      } else {
        setError(err.response?.data?.error || "Error al guardar. Intentá de nuevo.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 16px rgba(22,50,68,0.07)", marginBottom: 20, padding: "28px", textAlign: "center", color: "#8fa3b1", fontSize: 13 }}>
      Cargando datos personales…
    </div>
  );

  return (
    <SectionCard
      title="Datos personales"
      subtitle="Tu información como usuario de Fixo"
      action={<SaveButton onClick={handleSave} loading={saving} saved={saved} />}
    >
      {/* Nombre y apellido */}
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Nombre *</label>
          <input style={inputStyle} value={form.first_name} onChange={e => update("first_name", e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Apellido *</label>
          <input style={inputStyle} value={form.last_name} onChange={e => update("last_name", e.target.value)} />
        </div>
      </div>

      {/* Username */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Nombre de usuario</label>
        <input
          style={inputStyle}
          value={form.username}
          onChange={e => update("username", e.target.value)}
          placeholder="3-20 caracteres — letras, números y _"
        />
      </div>

      {/* Teléfono */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Teléfono</label>
        <ArgentinaPhoneInput
          value={form.phone}
          onChange={phone => update("phone", phone)}
        />
      </div>

      {/* Domicilio */}
      <div style={{ borderTop: "1px solid #f0f5f8", paddingTop: 16, marginTop: 4 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#8fa3b1", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
          Domicilio <span style={{ fontWeight: 400, textTransform: "none" }}>(opcional)</span>
        </div>
        {/* Botón detectar */}
        <div style={{ marginBottom: 14 }}>
          <button onClick={() => detect(result => setForm(f => ({ ...f, ...result })))} disabled={detecting}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 18px", borderRadius: 50, border: "1.5px solid rgba(22,50,68,0.2)", background: detecting ? "#f5f7fa" : "#fff", color: "#163244", fontFamily: font, fontWeight: 600, fontSize: 12, cursor: detecting ? "not-allowed" : "pointer" }}>
            {detecting ? <><span style={{ width: 13, height: 13, border: "2px solid #163244", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> Detectando…</> : <>📍 Detectar mi ubicación automáticamente</>}
          </button>
          {geoError && <div style={{ marginTop: 8, fontSize: 12, color: "#c0392b", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>⚠ {geoError} <button onClick={clearGeoError} style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 14 }}>✕</button></div>}
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Calle y número</label>
          <input style={inputStyle} value={form.street} onChange={e => update("street", e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Localidad</label>
            <input style={inputStyle} value={form.city} onChange={e => update("city", e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Provincia</label>
            <select style={inputStyle} value={form.province} onChange={e => update("province", e.target.value)}>
              <option value="">— Seleccioná —</option>
              {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div style={{ width: "40%" }}>
          <label style={labelStyle}>Código postal</label>
          <input style={inputStyle} value={form.postal_code} onChange={e => update("postal_code", e.target.value)} />
        </div>
      </div>

      <ErrorMsg msg={error} />
    </SectionCard>
  );
}

// ─── Sección servicios ────────────────────────────────────────────────────────
function ServicesSection({ services: initialServices }) {
  const [services,  setServices]  = useState(initialServices);
  const [catalog,   setCatalog]   = useState([]);
  const [adding,    setAdding]    = useState(false);
  const [saving,    setSaving]    = useState(null);
  const [editing,   setEditing]   = useState({});
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState("");

  const loadCatalog = async () => {
    if (catalog.length > 0) { setAdding(true); return; }
    try {
      const { data } = await api.get("/providers/catalog");
      setCatalog(data.services || data || []);
      setAdding(true);
    } catch { setError("Error cargando catálogo"); }
  };

  const handleAdd = async (serviceId) => {
    setSaving(serviceId); setError("");
    try {
      const { data } = await api.post("/providers/me/services", { service_id: serviceId });
      setServices(s => [...s, data.service]);
      setAdding(false);
      setSuccess("Servicio agregado correctamente");
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      setError(err.response?.data?.error || "Error al agregar servicio");
    } finally { setSaving(null); }
  };

  const handleRemove = async (s) => {
    if (!window.confirm(`¿Eliminar "${s.service_name || s.label}"? Esta acción no se puede deshacer.`)) return;
    setSaving(s.id); setError("");
    try {
      await api.delete(`/providers/me/services/${s.id}`);
      setServices(sv => sv.filter(sv => sv.id !== s.id));
      setSuccess("Servicio eliminado");
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      setError(err.response?.data?.error || "Error al eliminar servicio");
    } finally { setSaving(null); }
  };

  const startEdit = (s) => {
    setEditing(e => ({ ...e, [s.id]: {
      years_experience:       s.years_experience       || 0,
      base_price_reference:   s.base_price_reference   || "",
      estimated_duration_hs:  s.estimated_duration_hs  || "",
      emergency_enabled:      s.emergency_enabled      || false,
      quote_enabled:          s.quote_enabled          !== false,
    }}));
  };

  const cancelEdit = (id) => setEditing(e => { const n = { ...e }; delete n[id]; return n; });

  const updateDraft = (id, field, val) => setEditing(e => ({ ...e, [id]: { ...e[id], [field]: val } }));

  const handleSaveEdit = async (s) => {
    setSaving(s.id); setError("");
    try {
      const { data } = await api.patch(`/providers/me/services/${s.id}`, editing[s.id]);
      setServices(sv => sv.map(sv => sv.id === s.id ? { ...sv, ...data.service } : sv));
      cancelEdit(s.id);
      setSuccess("Servicio actualizado correctamente");
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar cambios");
    } finally { setSaving(null); }
  };

  const registeredIds = new Set(services.map(s => s.service_id));

  return (
    <SectionCard title="Mis servicios" subtitle="Configurá cada rubro que ofrecés"
      action={<button onClick={loadCatalog} style={{ padding: "8px 16px", borderRadius: 8, border: "2px solid #163244", background: "transparent", color: "#163244", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>+ Agregar servicio</button>}>
      {error   && <div style={{ background: "#fdecea", border: "1px solid #e57373", borderRadius: 8, padding: "9px 14px", fontSize: 13, color: "#c0392b", fontWeight: 600, marginBottom: 12 }}>⚠ {error}</div>}
      {success && <div style={{ background: "#e8f7ef", border: "1px solid #1b7f4e", borderRadius: 8, padding: "9px 14px", fontSize: 13, color: "#1b7f4e", fontWeight: 600, marginBottom: 12 }}>✓ {success}</div>}

      {adding && (
        <div style={{ background: "#fffbf2", border: "2px dashed #E7A432", borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#163244", marginBottom: 10 }}>Seleccioná un servicio para agregar:</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {catalog.filter(c => !registeredIds.has(c.id)).map(c => (
              <button key={c.id} onClick={() => handleAdd(c.id)} disabled={saving === c.id}
                style={{ padding: "6px 14px", borderRadius: 20, background: "#163244", border: "none", color: "#fff", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                {saving === c.id ? "⏳" : `${SERVICE_ICONS[c.service_name] || "🛠️"} ${c.service_name}`}
              </button>
            ))}
            {catalog.filter(c => !registeredIds.has(c.id)).length === 0 && <span style={{ fontSize: 13, color: "#8fa3b1" }}>Ya tenés todos los servicios disponibles registrados.</span>}
          </div>
          <button onClick={() => setAdding(false)} style={{ marginTop: 10, background: "none", border: "none", color: "#8fa3b1", fontSize: 12, cursor: "pointer", fontFamily: "Montserrat, sans-serif" }}>Cancelar</button>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {services.length === 0 && !adding && <div style={{ textAlign: "center", padding: 32, color: "#8fa3b1", fontSize: 13 }}>No tenés servicios registrados. Agregá tu primer rubro.</div>}
        {services.map((s) => {
          const isEditing = !!editing[s.id];
          const draft = editing[s.id] || {};
          return (
            <div key={s.id} style={{ borderRadius: 10, border: `1.5px solid ${isEditing ? "#163244" : "#e0e9ef"}`, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: isEditing ? "#f0f5f8" : "#fafafa" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 22 }}>{SERVICE_ICONS[s.label || s.service_name] || "🛠️"}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#163244" }}>{s.label || s.service_name}</div>
                    <div style={{ fontSize: 11, color: "#8fa3b1" }}>Nivel {s.validation_level}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Badge status={s.status} />
                  {!isEditing ? (
                    <>
                      <button onClick={() => startEdit(s)} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #163244", background: "transparent", color: "#163244", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 11, cursor: "pointer" }}>✏ Editar</button>
                      <button onClick={() => handleRemove(s)} disabled={saving === s.id} style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 15, padding: "4px 6px" }} title="Eliminar">{saving === s.id ? "⏳" : "🗑"}</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => handleSaveEdit(s)} disabled={saving === s.id} style={{ padding: "5px 14px", borderRadius: 6, background: "#163244", border: "none", color: "#fff", fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: 11, cursor: saving === s.id ? "not-allowed" : "pointer" }}>{saving === s.id ? "⏳" : "Guardar cambios"}</button>
                      <button onClick={() => cancelEdit(s.id)} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #cddae4", background: "transparent", color: "#5c7a8a", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 11, cursor: "pointer" }}>Cancelar</button>
                    </>
                  )}
                </div>
              </div>
              <div style={{ padding: "14px 16px", background: "#fff", borderTop: "1px solid #f0f5f8" }}>
                {isEditing ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                    <div style={{ flex: "1 1 140px" }}>
                      <label style={labelStyle}>Años de experiencia</label>
                      <input type="number" min={0} max={50} value={draft.years_experience} onChange={e => updateDraft(s.id, "years_experience", Number(e.target.value))} style={inputStyle} />
                    </div>
                    <div style={{ flex: "1 1 180px" }}>
                      <label style={labelStyle}>Precio base referencial (ARS)</label>
                      <input type="number" min={0} value={draft.base_price_reference} onChange={e => updateDraft(s.id, "base_price_reference", e.target.value)} placeholder="Ej: 15000" style={inputStyle} />
                    </div>
                    <div style={{ flex: "1 1 160px" }}>
                      <label style={labelStyle}>Duración estimada (hs)</label>
                      <input type="number" min={0.5} max={24} step={0.5} value={draft.estimated_duration_hs} onChange={e => updateDraft(s.id, "estimated_duration_hs", e.target.value)} placeholder="Ej: 2.5" style={inputStyle} />
                      <div style={{ fontSize: 10, color: "#8fa3b1", marginTop: 3 }}>Tiempo típico que te lleva este servicio</div>
                    </div>
                    <div style={{ flex: "1 1 100%", display: "flex", gap: 24, marginTop: 4, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div onClick={() => updateDraft(s.id, "emergency_enabled", !draft.emergency_enabled)} style={{ width: 40, height: 22, borderRadius: 11, cursor: "pointer", background: draft.emergency_enabled ? "#163244" : "#c8d4dc", position: "relative", transition: "background 0.25s", flexShrink: 0 }}>
                          <div style={{ position: "absolute", top: 3, left: draft.emergency_enabled ? 20 : 3, width: 16, height: 16, borderRadius: "50%", background: draft.emergency_enabled ? "#E7A432" : "#fff", transition: "left 0.25s" }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#163244" }}>Acepta urgencias</div>
                          <div style={{ fontSize: 11, color: "#8fa3b1" }}>El cliente puede solicitarte con urgencia</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div onClick={() => updateDraft(s.id, "quote_enabled", !draft.quote_enabled)} style={{ width: 40, height: 22, borderRadius: 11, cursor: "pointer", background: draft.quote_enabled ? "#163244" : "#c8d4dc", position: "relative", transition: "background 0.25s", flexShrink: 0 }}>
                          <div style={{ position: "absolute", top: 3, left: draft.quote_enabled ? 20 : 3, width: 16, height: 16, borderRadius: "50%", background: draft.quote_enabled ? "#E7A432" : "#fff", transition: "left 0.25s" }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#163244" }}>Acepta presupuesto previo</div>
                          <div style={{ fontSize: 11, color: "#8fa3b1" }}>Podés cotizar antes de confirmar</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 13 }}><span style={{ color: "#8fa3b1" }}>Experiencia: </span><strong style={{ color: "#163244" }}>{s.years_experience || 0} años</strong></div>
                    {s.base_price_reference && <div style={{ fontSize: 13 }}><span style={{ color: "#8fa3b1" }}>Precio base: </span><strong style={{ color: "#163244" }}>${Number(s.base_price_reference).toLocaleString("es-AR")}</strong></div>}
                    {s.estimated_duration_hs && <div style={{ fontSize: 13 }}><span style={{ color: "#8fa3b1" }}>Duración típica: </span><strong style={{ color: "#163244" }}>{s.estimated_duration_hs}hs</strong></div>}
                    <div style={{ fontSize: 13 }}><span style={{ color: "#8fa3b1" }}>Urgencias: </span><strong style={{ color: s.emergency_enabled ? "#1b7f4e" : "#c0392b" }}>{s.emergency_enabled ? "✓ Sí" : "✕ No"}</strong></div>
                    <div style={{ fontSize: 13 }}><span style={{ color: "#8fa3b1" }}>Presupuesto previo: </span><strong style={{ color: s.quote_enabled ? "#1b7f4e" : "#c0392b" }}>{s.quote_enabled ? "✓ Sí" : "✕ No"}</strong></div>
                  </div>
                )}
                {s.status === "pending_documents" && <div style={{ background: "#fff3e0", border: "1px solid #E7A432", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#b06a00", marginTop: 12 }}>📄 Necesitás cargar documentación. Ir al tab <strong>Documentos</strong>.</div>}
                {s.status === "pending_review"    && <div style={{ background: "#e8f7ef", border: "1px solid #1b7f4e", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#1b7f4e", marginTop: 12 }}>✓ Documentación enviada. En revisión por el equipo de Fixo.</div>}
                {s.status === "rejected"          && <div style={{ background: "#fdecea", border: "1px solid #e57373", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#c0392b", marginTop: 12 }}>✕ Documentación rechazada. Subí documentación actualizada en el tab <strong>Documentos</strong>.</div>}
                {s.status === "expired"           && <div style={{ background: "#f3eeff", border: "1px solid #7b5ea7", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#7b5ea7", marginTop: 12 }}>⚠ Documentación vencida. Actualizá tus documentos en el tab <strong>Documentos</strong>.</div>}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

// ─── Sección cobertura ────────────────────────────────────────────────────────
function CoverageSection({ coverage: initialCoverage }) {
  const normalize = (zones) => zones.map(z => ({
    ...z,
    id:       Number(z.id),   // asegurar que el id sea siempre número
    provincia: z.provincia || z.province      || "",
    ciudad:    z.ciudad    || z.city          || "",
    barrio:    z.barrio    || z.neighborhood  || "",
    radio_km:  Number(z.radio_km || z.radius_km) || 10,
    _new:      z._new || false,
  }));

  const [zonas,    setZonas]    = useState(() => normalize(initialCoverage));
  const [saving,   setSaving]   = useState(null);
  const [error,    setError]    = useState("");
  const [editing,  setEditing]  = useState({}); // { [id]: draft }

  const addZona = () => {
    const tempId = `new-${Date.now()}`;
    setZonas(z => [...z, { id: tempId, provincia: "", ciudad: "", barrio: "", radio_km: 10, _new: true }]);
    setEditing(e => ({ ...e, [tempId]: true }));
  };

  const startEdit = (id) => setEditing(e => ({ ...e, [id]: true }));
  const cancelEdit = (zona) => {
    if (zona._new) {
      setZonas(z => z.filter(z => z.id !== zona.id));
    }
    setEditing(e => { const n = { ...e }; delete n[zona.id]; return n; });
  };

  const updateZona = (id, field, val) => {
    setZonas(z => z.map(z => z.id === id ? { ...z, [field]: val } : z));
  };

  const saveZona = async (zona) => {
    if (!zona.provincia || !zona.ciudad) {
      setError("Provincia y ciudad son obligatorias.");
      return;
    }
    setSaving(zona.id); setError("");
    try {
      const payload = {
        province:     zona.provincia,
        city:         zona.ciudad,
        neighborhood: zona.barrio || "",
        radius_km:    zona.radio_km,
      };
      if (zona._new) {
        const res = await api.post("/providers/me/coverage", payload);
        const saved = normalize([res.data.coverage])[0];
        setZonas(z => z.map(z => z.id === zona.id ? saved : z));
      } else {
        const res = await api.put(`/providers/me/coverage/${zona.id}`, payload);
        const saved = normalize([res.data.coverage])[0];
        setZonas(z => z.map(z => z.id === zona.id ? saved : z));
      }
      setEditing(e => { const n = { ...e }; delete n[zona.id]; return n; });
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar la zona.");
    } finally {
      setSaving(null);
    }
  };

  const removeZona = async (zona) => {
    if (!window.confirm("¿Eliminar esta zona de cobertura?")) return;
    setSaving(zona.id); setError("");
    try {
      if (!String(zona.id).startsWith("new-")) {
        await api.delete(`/providers/me/coverage/${zona.id}`);
      }
      setZonas(z => z.filter(z => z.id !== zona.id));
      setEditing(e => { const n = { ...e }; delete n[zona.id]; return n; });
    } catch (err) {
      setError(err.response?.data?.error || "Error al eliminar la zona.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <SectionCard
      title="Zonas de cobertura"
      subtitle="Áreas donde ofrecés tus servicios"
      action={
        <button onClick={addZona} style={{ padding: "8px 16px", borderRadius: 8, border: "2px solid #163244", background: "transparent", color: "#163244", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
          + Agregar zona
        </button>
      }
    >
      {error && (
        <div style={{ background: "#fdecea", border: "1px solid #e57373", borderRadius: 8, padding: "9px 14px", fontSize: 13, color: "#c0392b", fontWeight: 600, marginBottom: 12 }}>
          ⚠ {error}
        </div>
      )}

      {zonas.map((z, i) => {
        const isEditing = !!editing[z.id];
        return (
          <div key={z.id} style={{
            background: isEditing ? "#fffbf2" : "#f7fafc",
            borderRadius: 10, padding: "14px 16px", marginBottom: 12,
            border: `1.5px solid ${isEditing ? "#E7A432" : "#e0e9ef"}`,
          }}>
            {/* Cabecera */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isEditing ? 12 : 0 }}>
              <span style={{ fontWeight: 700, fontSize: 12, color: "#163244", background: isEditing ? "#E7A432" : "#e7f0f5", padding: "2px 10px", borderRadius: 20 }}>
                {z._new ? "Nueva zona" : `Zona ${i + 1}`}
              </span>

              {/* Vista compacta cuando no se edita */}
              {!isEditing && (
                <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, marginLeft: 12 }}>
                  <span style={{ fontSize: 13, color: "#163244", fontWeight: 600 }}>
                    {z.provincia}{z.ciudad ? `, ${z.ciudad}` : ""}{z.barrio ? ` · ${z.barrio}` : ""}
                  </span>
                  <span style={{ fontSize: 12, color: "#8fa3b1" }}>📍 {z.radio_km} km</span>
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                {isEditing ? (
                  <>
                    <button
                      onClick={() => saveZona(z)}
                      disabled={saving === z.id}
                      style={{ padding: "4px 14px", borderRadius: 6, background: "#163244", border: "none", color: "#fff", fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: 11, cursor: saving === z.id ? "not-allowed" : "pointer" }}
                    >
                      {saving === z.id ? "⏳" : "Guardar"}
                    </button>
                    <button
                      onClick={() => cancelEdit(z)}
                      disabled={saving === z.id}
                      style={{ padding: "4px 12px", borderRadius: 6, background: "transparent", border: "1px solid #cddae4", color: "#5c7a8a", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 11, cursor: "pointer" }}
                    >
                      Cancelar
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => startEdit(z.id)}
                      style={{ padding: "4px 12px", borderRadius: 6, background: "transparent", border: "1px solid #163244", color: "#163244", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 11, cursor: "pointer" }}
                    >
                      ✏ Editar
                    </button>
                    <button
                      onClick={() => removeZona(z)}
                      disabled={saving === z.id}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 15, padding: "4px 6px" }}
                      title="Eliminar zona"
                    >
                      🗑
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Formulario de edición */}
            {isEditing && (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 140px" }}>
                  <label style={labelStyle}>Provincia *</label>
                  <select style={{ ...inputStyle, cursor: "pointer" }} value={z.provincia} onChange={e => updateZona(z.id, "provincia", e.target.value)}>
                    <option value="">Seleccioná...</option>
                    {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div style={{ flex: "1 1 140px" }}>
                  <label style={labelStyle}>Ciudad *</label>
                  <input style={inputStyle} value={z.ciudad} placeholder="Ej: Neuquén" onChange={e => updateZona(z.id, "ciudad", e.target.value)} />
                </div>
                <div style={{ flex: "1 1 120px" }}>
                  <label style={labelStyle}>Barrio</label>
                  <input style={inputStyle} value={z.barrio || ""} placeholder="Opcional" onChange={e => updateZona(z.id, "barrio", e.target.value)} />
                </div>
                <div style={{ flex: "1 1 180px" }}>
                  <label style={labelStyle}>Radio de cobertura: {z.radio_km} km</label>
                  <input
                    type="range" min={1} max={100} step={1}
                    value={z.radio_km}
                    onChange={e => updateZona(z.id, "radio_km", Number(e.target.value))}
                    style={{ width: "100%", accentColor: "#E7A432", marginTop: 10 }}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#8fa3b1" }}>
                    <span>1 km</span><span>100 km</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {zonas.length === 0 && (
        <div style={{ textAlign: "center", padding: "32px", color: "#8fa3b1", background: "#f7fafc", borderRadius: 10, border: "2px dashed #e0e9ef", fontSize: 13 }}>
          📍 No tenés zonas configuradas. Agregá tu primera zona.
        </div>
      )}
    </SectionCard>
  );
}

// ─── Sección disponibilidad ───────────────────────────────────────────────────
// ─── TimeSelect: selector de hora en formato 24hs ────────────────────────────
function TimeSelect({ value, onChange }) {
  const HOURS   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  const MINUTES = ["00", "15", "30", "45"];

  const [h, m] = (value || "09:00").split(":");
  const hour   = HOURS.includes(h)   ? h   : "09";
  const minute = MINUTES.includes(m) ? m   : "00";

  const handleH = (e) => onChange(`${e.target.value}:${minute}`);
  const handleM = (e) => onChange(`${hour}:${e.target.value}`);

  const selectStyle = {
    ...inputStyle,
    width: "auto", padding: "6px 8px", fontSize: 13, cursor: "pointer",
    minWidth: 54,
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
      <select value={hour} onChange={handleH} style={selectStyle}>
        {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span style={{ color: "#8fa3b1", fontWeight: 700 }}>:</span>
      <select value={minute} onChange={handleM} style={selectStyle}>
        {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
      </select>
    </div>
  );
}

function AvailabilitySection({ availability: initialAvail, onUpdate }) {
  // Estructura base de los 7 días
  const DEFAULT_DAYS = DIAS_LABELS.map((label, i) => ({
    day_of_week: i,
    start_time:  "09:00",
    end_time:    "18:00",
    available:   false,
  }));

  // Normaliza y fusiona los datos del backend con los 7 días base
  const buildAvail = (data) => {
    if (!data || data.length === 0) return DEFAULT_DAYS;
    return DEFAULT_DAYS.map(def => {
      const match = data.find(d => (d.day_of_week ?? d.day) === def.day_of_week);
      if (!match) return def;
      return {
        ...def,
        start_time: match.start_time || match.start || "09:00",
        end_time:   match.end_time   || match.end   || "18:00",
        available:  match.available  ?? true,
      };
    });
  };

  const [avail,   setAvail]   = useState(() => buildAvail(initialAvail));
  const [loading, setLoading] = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");
  // El backend avisa si se guardó sin ningún día disponible: el prestador
  // queda fuera del matching y no recibe solicitudes nuevas.
  const [advertencia, setAdvertencia] = useState("");

  const update = (i, field, val) => {
    setAvail(a => a.map((d, idx) => idx === i ? { ...d, [field]: val } : d));
    setSaved(false);
  };

  const handleSave = async () => {
    setLoading(true); setError(""); setAdvertencia("");
    try {
      const { data } = await api.put("/providers/me/availability", { availability: avail });
      setAdvertencia(data.advertencia || "");
      // Actualizar el state del padre con los datos frescos del backend
      if (onUpdate && data.availability) {
        onUpdate(data.availability);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar la disponibilidad.");
    } finally {
      setLoading(false);
    }
  };

  const totalHours = avail
    .filter(d => d.available)
    .reduce((sum, d) => {
      const h = parseFloat(calcHours(d.start_time, d.end_time));
      return sum + (isNaN(h) ? 0 : h);
    }, 0);

  return (
    <SectionCard
      title="Disponibilidad semanal"
      subtitle={`${avail.filter(d => d.available).length} días activos · ${totalHours.toFixed(1)} hs semanales`}
      action={<SaveButton onClick={handleSave} loading={loading} saved={saved} />}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {avail.map((d, i) => {
          const label = DIAS_LABELS[d.day_of_week ?? i];
          return (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 10,
              background: d.available ? "#f7fafc" : "#fafafa",
              border: `1.5px solid ${d.available ? "#E7A432" : "#e0e9ef"}`,
              transition: "all 0.2s",
            }}>
              {/* Toggle */}
              <div
                onClick={() => update(i, "available", !d.available)}
                style={{
                  width: 40, height: 22, borderRadius: 11, cursor: "pointer",
                  background: d.available ? "#163244" : "#c8d4dc",
                  position: "relative", transition: "background 0.25s", flexShrink: 0,
                }}
              >
                <div style={{
                  position: "absolute", top: 3,
                  left: d.available ? 20 : 3,
                  width: 16, height: 16, borderRadius: "50%",
                  background: d.available ? "#E7A432" : "#fff",
                  transition: "left 0.25s",
                }} />
              </div>

              {/* Día */}
              <span style={{ width: 36, fontWeight: 700, fontSize: 13, color: d.available ? "#163244" : "#8fa3b1" }}>
                {label}
              </span>

              {/* Horarios */}
              {d.available ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                  <TimeSelect value={d.start_time} onChange={val => update(i, "start_time", val)} />
                  <span style={{ color: "#8fa3b1", fontSize: 12 }}>a</span>
                  <TimeSelect value={d.end_time} onChange={val => update(i, "end_time", val)} />
                  <span style={{ fontSize: 11, color: "#8fa3b1", marginLeft: "auto", whiteSpace: "nowrap" }}>
                    {calcHours(d.start_time, d.end_time)} hs
                  </span>
                </div>
              ) : (
                <span style={{ fontSize: 13, color: "#aab8c2", fontStyle: "italic" }}>No disponible</span>
              )}
            </div>
          );
        })}
      </div>
      <p style={{ fontSize: 12, color: "#8fa3b1", marginTop: 12, marginBottom: 0 }}>
        Activá los días con el toggle y configurá el horario. Luego presioná <strong>Guardar cambios</strong>.
      </p>
      <ErrorMsg msg={error} />
      {advertencia && (
        <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 10, background: "rgba(231,164,50,0.1)", border: "1px solid rgba(231,164,50,0.4)", color: "#163244", fontSize: 12, fontWeight: 600 }}>
          {advertencia}
        </div>
      )}
    </SectionCard>
  );
}

// ─── Sección documentos ───────────────────────────────────────────────────────

const CLOUDINARY_CLOUD  = 'di1nylozi';
const CLOUDINARY_PRESET = 'fixo_docs';
const MAX_FILE_MB       = 10;

// Los valores son los del enum document_type de la base (matricula, permiso,
// certificado, habilitacion, identidad, otro), los mismos que usa la app
// ((prestador)/perfil.tsx). Hasta el 27-10-2026 la web mandaba 'dni',
// 'habilitacion_municipal' y 'seguro', que la base rechaza: esa subida fallaba
// siempre. El seguro va como 'otro' (decisión de Diego, sin migración).
const DOC_TYPES = [
  { value: 'identidad',    label: 'DNI / Identificación',                     icon: '🪪' },
  { value: 'matricula',    label: 'Matrícula profesional',                    icon: '🎓' },
  { value: 'permiso',      label: 'Permiso',                                  icon: '📝' },
  { value: 'habilitacion', label: 'Habilitación municipal',                   icon: '🏛️' },
  { value: 'certificado',  label: 'Certificado de gasfitería / electricidad', icon: '📋' },
  { value: 'otro',         label: 'Otro (por ejemplo, seguro de responsabilidad civil)', icon: '📄' },
];

const DOC_STATUS_CONFIG = {
  pending:        { label: 'Pendiente',   color: '#b06a00', bg: '#fff3e0' },
  pending_review: { label: 'En revisión', color: '#b06a00', bg: '#fff3e0' },
  approved:       { label: 'Aprobado',    color: '#1b7f4e', bg: '#e8f7ef' },
  rejected:       { label: 'Rechazado',   color: '#c0392b', bg: '#fdecea' },
  expired:        { label: 'Vencido',     color: '#7b5ea7', bg: '#f3eeff' },
};

function DocBadge({ status }) {
  const cfg = DOC_STATUS_CONFIG[status] || { label: status, color: '#5c7a8a', bg: '#f0f5f8' };
  return (
    <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: cfg.color, background: cfg.bg }}>
      {cfg.label.toUpperCase()}
    </span>
  );
}

// Sube el archivo a Cloudinary y devuelve la URL pública
async function uploadToCloudinary(file) {
  const fd = new FormData();
  fd.append('file',         file);
  fd.append('upload_preset', CLOUDINARY_PRESET);
  fd.append('folder',       'fixo/documentos');

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/auto/upload`,
    { method: 'POST', body: fd }
  );
  if (!res.ok) throw new Error('Error al subir a Cloudinary');
  const data = await res.json();
  return data.secure_url;
}

function DocumentsSection({ services }) {
  const EMPTY_FORM = { document_type: '', provider_service_id: '', expires_at: '', file: null };

  const [documents, setDocuments] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadStep, setUploadStep] = useState(''); // 'cloudinary' | 'backend' | ''
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [showForm,  setShowForm]  = useState(false);

  const fetchDocs = async () => {
    try {
      const { data } = await api.get('/providers/me/documents');
      // El backend devuelve el array directo (no { documents: [] })
      setDocuments(Array.isArray(data) ? data : data.documents || []);
    } catch {
      setError('Error cargando documentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDocs(); }, []);

  const handleUpload = async () => {
    setError('');
    if (!form.document_type) { setError('Seleccioná el tipo de documento.'); return; }
    if (!form.file)          { setError('Seleccioná un archivo para subir.'); return; }
    if (form.file.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`El archivo supera el límite de ${MAX_FILE_MB} MB.`);
      return;
    }

    setUploading(true);
    setSuccess('');
    try {
      // 1. Subir archivo a Cloudinary
      setUploadStep('cloudinary');
      const file_url = await uploadToCloudinary(form.file);

      // 2. Registrar en el backend con los campos correctos
      setUploadStep('backend');
      await api.post('/providers/me/documents', {
        document_type:       form.document_type,
        file_url,
        provider_service_id: form.provider_service_id || null,
        expires_at:          form.expires_at           || null,
      });

      setSuccess('Documento subido correctamente. Quedará pendiente de revisión.');
      setForm(EMPTY_FORM);
      setShowForm(false);
      await fetchDocs();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al subir el documento. Intentá de nuevo.');
    } finally {
      setUploading(false);
      setUploadStep('');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    setError(''); setSuccess('');
    try {
      await api.delete(`/providers/me/documents/${docId}`);
      setSuccess('Documento eliminado.');
      await fetchDocs();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar.');
    }
  };

  const cancelForm = () => { setShowForm(false); setError(''); setForm(EMPTY_FORM); };

  const docTypeLabel = (val) => DOC_TYPES.find(d => d.value === val)?.label || val;
  const docTypeIcon  = (val) => DOC_TYPES.find(d => d.value === val)?.icon  || '📄';

  const uploadLabel = uploadStep === 'cloudinary'
    ? 'Subiendo archivo…'
    : uploadStep === 'backend'
    ? 'Registrando documento…'
    : 'Subir documento';

  return (
    <SectionCard
      title="Documentación"
      subtitle="Subí los documentos requeridos para activar tus servicios"
      action={
        !showForm && (
          <button onClick={() => setShowForm(true)} style={{
            padding: '8px 20px', borderRadius: 8, background: '#163244',
            color: '#fff', border: 'none', fontFamily: 'Montserrat, sans-serif',
            fontWeight: 600, fontSize: 13, cursor: 'pointer',
          }}>
            + Subir documento
          </button>
        )
      }
    >
      {/* Formulario de upload */}
      {showForm && (
        <div style={{ background: '#f0f5f8', borderRadius: 12, padding: 20, marginBottom: 24, border: '2px dashed #E7A432' }}>
          <div style={{ fontWeight: 700, color: '#163244', marginBottom: 16 }}>Nuevo documento</div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            {/* Tipo de documento */}
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Tipo de documento *</label>
              <select
                value={form.document_type}
                onChange={e => setForm(f => ({ ...f, document_type: e.target.value }))}
                style={inputStyle}
              >
                <option value="">Seleccioná…</option>
                {DOC_TYPES.map(d => (
                  <option key={d.value} value={d.value}>{d.icon} {d.label}</option>
                ))}
              </select>
            </div>

            {/* Servicio (opcional) */}
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Servicio (opcional)</label>
              <select
                value={form.provider_service_id}
                onChange={e => setForm(f => ({ ...f, provider_service_id: e.target.value }))}
                style={inputStyle}
              >
                <option value="">General (todos los servicios)</option>
                {(services || []).map(s => (
                  <option key={s.id} value={s.id}>
                    {s.service_name || s.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Fecha de vencimiento */}
            <div style={{ flex: '1 1 160px' }}>
              <label style={labelStyle}>Vencimiento (opcional)</label>
              <input
                type="date"
                value={form.expires_at}
                onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                style={inputStyle}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          {/* Input de archivo */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Archivo * (JPG, PNG, PDF, WEBP — máx. {MAX_FILE_MB} MB)</label>
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.pdf"
              onChange={e => setForm(f => ({ ...f, file: e.target.files[0] || null }))}
              style={{ fontSize: 13, fontFamily: 'Montserrat, sans-serif' }}
            />
            {form.file && (
              <div style={{ fontSize: 12, color: '#1b7f4e', marginTop: 4 }}>
                ✓ {form.file.name} ({(form.file.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>

          {error && (
            <div style={{ background: '#fdecea', border: '1px solid #e57373', borderRadius: 8, padding: '9px 14px', fontSize: 13, color: '#c0392b', fontWeight: 600, marginBottom: 12 }}>
              ⚠ {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleUpload} disabled={uploading} style={{
              padding: '9px 22px', borderRadius: 8,
              background: uploading ? '#8fa3b1' : '#163244',
              color: '#fff', border: 'none', fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600, fontSize: 13,
              cursor: uploading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {uploading && (
                <span style={{ width: 13, height: 13, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
              )}
              {uploading ? uploadLabel : '☁ Subir documento'}
            </button>
            <button onClick={cancelForm} disabled={uploading} style={{
              padding: '9px 22px', borderRadius: 8, background: 'transparent',
              color: '#5c7a8a', border: '1.5px solid #cddae4',
              fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: 13,
              cursor: uploading ? 'not-allowed' : 'pointer',
            }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Mensaje de éxito */}
      {success && (
        <div style={{ background: '#e8f7ef', border: '1px solid #1b7f4e', borderRadius: 8, padding: '9px 14px', fontSize: 13, color: '#1b7f4e', fontWeight: 600, marginBottom: 16 }}>
          ✓ {success}
        </div>
      )}

      {/* Lista de documentos */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 32, color: '#8fa3b1' }}>Cargando documentos…</div>
      ) : documents.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#8fa3b1' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📂</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Sin documentos cargados</div>
          <div style={{ fontSize: 13 }}>Subí tu documentación para que el equipo de Fixo pueda revisar tu perfil.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {documents.map(doc => (
            <div key={doc.id} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              background: '#f8fafc', border: '1px solid #e2eaf0',
              borderRadius: 10, padding: '12px 16px',
            }}>
              <span style={{ fontSize: 22 }}>{docTypeIcon(doc.document_type)}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#163244' }}>
                  {docTypeLabel(doc.document_type)}
                  {doc.service_name && (
                    <span style={{ fontWeight: 400, color: '#5c7a8a', marginLeft: 6, fontSize: 12 }}>
                      · {doc.service_name}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#8fa3b1', marginTop: 2 }}>
                  Subido: {new Date(doc.created_at).toLocaleDateString('es-AR')}
                  {doc.expires_at && ` · Vence: ${formatearFecha(doc.expires_at)}`}
                </div>
              </div>
              {/* Campo correcto: verification_status */}
              <DocBadge status={doc.verification_status} />
              {doc.file_url && (
                <a href={doc.file_url} target="_blank" rel="noreferrer" style={{
                  padding: '5px 12px', borderRadius: 6, background: '#e3eef5',
                  color: '#163244', fontSize: 12, fontWeight: 600, textDecoration: 'none',
                }}>
                  Ver
                </a>
              )}
              {/* Solo se puede eliminar si no está aprobado */}
              {doc.verification_status !== 'approved' && (
                <button onClick={() => handleDelete(doc.id)} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#c0392b', fontSize: 16, padding: '4px 6px',
                }} title="Eliminar">
                  🗑
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}


// ─── Sección calificaciones ───────────────────────────────────────────────────
function RatingsSection({ providerId }) {
  const [ratings,  setRatings]  = useState([]);
  const [summary,  setSummary]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [replies,  setReplies]  = useState({}); // { [ratingId]: draft }
  const [saving,   setSaving]   = useState(null);
  const [success,  setSuccess]  = useState("");

  useEffect(() => {
    if (!providerId) return;
    setLoading(true);
    api.get(`/ratings/providers/provider/${providerId}?limit=50`)
      .then(res => {
        setRatings(res.data.ratings || []);
        setSummary(res.data.summary || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [providerId]);

  const startReply = (id, current) => setReplies(r => ({ ...r, [id]: current || "" }));
  const cancelReply = (id) => setReplies(r => { const n = { ...r }; delete n[id]; return n; });

  const handleSaveReply = async (ratingId) => {
    setSaving(ratingId);
    try {
      await api.patch(`/ratings/providers/reply/${ratingId}`, { reply: replies[ratingId] });
      setRatings(rs => rs.map(r => r.id === ratingId ? { ...r, provider_reply: replies[ratingId] } : r));
      cancelReply(ratingId);
      setSuccess("Respuesta guardada");
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      console.error("Error al guardar respuesta:", err);
    } finally {
      setSaving(null);
    }
  };

  const stars = (score, size = 14) => (
    <span style={{ color: "#E7A432", fontSize: size, letterSpacing: 1 }}>
      {"★".repeat(score)}
      <span style={{ color: "#e0e9ef" }}>{"★".repeat(5 - score)}</span>
    </span>
  );

  if (loading) return (
    <div style={{ textAlign: "center", padding: 40, color: "#8fa3b1", fontSize: 13 }}>
      Cargando calificaciones...
    </div>
  );

  return (
    <SectionCard
      title="Mis calificaciones"
      subtitle={summary ? `${summary.avg || "—"} promedio · ${summary.total} reseñas` : "Sin calificaciones aún"}
    >
      {success && (
        <div style={{ background: "#e8f7ef", border: "1px solid #1b7f4e", borderRadius: 8, padding: "9px 14px", fontSize: 13, color: "#1b7f4e", fontWeight: 600, marginBottom: 12 }}>
          ✓ {success}
        </div>
      )}

      {/* Resumen de estrellas */}
      {summary && parseInt(summary.total) > 0 && (
        <div style={{ background: "#f7fafc", borderRadius: 12, padding: "16px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 24 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: "#163244" }}>{summary.avg || "—"}</div>
            <div style={{ marginTop: 4 }}>{stars(Math.round(parseFloat(summary.avg) || 0), 18)}</div>
            <div style={{ fontSize: 11, color: "#8fa3b1", marginTop: 4 }}>{summary.total} reseña{summary.total !== "1" ? "s" : ""}</div>
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
            {[
              { stars: 5, count: summary.five_stars },
              { stars: 4, count: summary.four_stars },
              { stars: 3, count: summary.three_stars },
              { stars: 2, count: parseInt(summary.low_stars || 0) > 0 ? "1+" : 0 },
            ].map(row => {
              const pct = summary.total > 0 ? (parseInt(row.count) / parseInt(summary.total)) * 100 : 0;
              return (
                <div key={row.stars} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
                  <span style={{ color: "#8fa3b1", width: 14, textAlign: "right" }}>{row.stars}</span>
                  <span style={{ color: "#E7A432", fontSize: 10 }}>★</span>
                  <div style={{ flex: 1, height: 6, background: "#e0e9ef", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: "#E7A432", borderRadius: 3 }} />
                  </div>
                  <span style={{ color: "#8fa3b1", width: 20 }}>{row.count || 0}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Lista de reseñas */}
      {ratings.length === 0 ? (
        <div style={{ textAlign: "center", padding: 32, color: "#8fa3b1", fontSize: 13 }}>
          Todavía no recibiste ninguna calificación.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {ratings.map(r => {
            const isReplying = replies[r.id] !== undefined;
            return (
              <div key={r.id} style={{ borderRadius: 10, border: "1.5px solid #e0e9ef", overflow: "hidden" }}>
                {/* Header */}
                <div style={{ padding: "12px 16px", background: "#fafafa", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#163244", marginBottom: 3 }}>
                      {r.client_name}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {stars(r.score)}
                      <span style={{ fontSize: 11, color: "#8fa3b1" }}>
                        · {r.service_key} · {new Date(r.created_at).toLocaleDateString("es-AR")}
                      </span>
                    </div>
                  </div>
                  {!isReplying && (
                    <button
                      onClick={() => startReply(r.id, r.provider_reply)}
                      style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #163244", background: "transparent", color: "#163244", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 11, cursor: "pointer" }}
                    >
                      {r.provider_reply ? "✏ Editar respuesta" : "Responder"}
                    </button>
                  )}
                </div>

                <div style={{ padding: "12px 16px", background: "#fff" }}>
                  {/* Comentario del cliente */}
                  {r.comment && (
                    <div style={{ fontSize: 13, color: "#5c7a8a", lineHeight: 1.6, marginBottom: r.provider_reply || isReplying ? 12 : 0 }}>
                      "{r.comment}"
                    </div>
                  )}
                  {!r.comment && !r.provider_reply && !isReplying && (
                    <div style={{ fontSize: 12, color: "#aab8c2", fontStyle: "italic" }}>Sin comentario.</div>
                  )}

                  {/* Respuesta existente */}
                  {r.provider_reply && !isReplying && (
                    <div style={{ background: "#f0f5f8", borderRadius: 8, padding: "10px 14px", borderLeft: "3px solid #163244", fontSize: 12, color: "#163244", lineHeight: 1.6 }}>
                      <span style={{ fontWeight: 700, fontSize: 11, color: "#8fa3b1", display: "block", marginBottom: 4 }}>TU RESPUESTA</span>
                      {r.provider_reply}
                    </div>
                  )}

                  {/* Editor de respuesta */}
                  {isReplying && (
                    <div>
                      <textarea
                        style={{ ...inputStyle, minHeight: 72, resize: "vertical", lineHeight: 1.6, fontSize: 13 }}
                        placeholder="Escribi tu respuesta..."
                        value={replies[r.id]}
                        onChange={e => setReplies(rv => ({ ...rv, [r.id]: e.target.value }))}
                      />
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <button
                          onClick={() => handleSaveReply(r.id)}
                          disabled={saving === r.id || !replies[r.id]?.trim()}
                          style={{ padding: "6px 16px", borderRadius: 6, background: "#163244", border: "none", color: "#fff", fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                        >
                          {saving === r.id ? "Guardando..." : "Guardar respuesta"}
                        </button>
                        <button
                          onClick={() => cancelReply(r.id)}
                          style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #cddae4", background: "transparent", color: "#5c7a8a", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────
function LoadingSkeleton() {
  return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa", fontFamily: font }}>
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "32px 24px" }}>
        <div style={{ background: "#163244", borderRadius: 16, height: 120, marginBottom: 18, opacity: 0.4 }} />
        <div style={{ background: "#fff", borderRadius: 12, height: 48, marginBottom: 18, opacity: 0.6 }} />
        <div style={{ background: "#fff", borderRadius: 16, height: 200, opacity: 0.6 }} />
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProviderProfilePage() {
  const [provider,  setProvider]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [activeTab, setActiveTab] = useState("perfil");

  // Cargar perfil desde GET /providers/me
  useEffect(() => {
    api.get("/providers/me")
      .then(res => {
        // El backend puede devolver { provider: {...} } o el objeto directo
        const data = res.data.provider || res.data;
        // Normalizar availability para que tenga label
        if (data.availability) {
          data.availability = data.availability.map(a => ({
            ...a,
            label: DIAS_LABELS[a.day_of_week ?? a.day ?? 0],
          }));
        }
        setProvider(data);
      })
      .catch(err => {
        console.error("Error cargando perfil:", err);
        setError("No se pudo cargar el perfil. Intentá recargar la página.");
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSkeleton />;

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: "#f5f7fa", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: font }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: 40, textAlign: "center", maxWidth: 400, border: "1px solid rgba(0,0,0,0.07)" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
          <div style={{ fontWeight: 700, color: "#163244", marginBottom: 8 }}>Error al cargar el perfil</div>
          <div style={{ color: "#7a93a4", fontSize: 14, marginBottom: 20 }}>{error}</div>
          <button onClick={() => window.location.reload()} style={{ background: "#163244", color: "#fff", border: "none", borderRadius: 50, padding: "10px 24px", fontFamily: font, fontWeight: 700, cursor: "pointer" }}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const p = provider;

  const TABS = [
    { id: "perfil",         label: "Perfil",         icon: "👤" },
    { id: "datos",          label: "Datos pers.",     icon: "🪪" },
    { id: "servicios",      label: "Servicios",       icon: "🛠️" },
    { id: "cobertura",      label: "Cobertura",       icon: "📍" },
    { id: "disponibilidad", label: "Disponibilidad",  icon: "📅" },
    { id: "documentos",     label: "Documentos",      icon: "📄" },
    { id: "calificaciones", label: "Calificaciones",  icon: "⭐" },
  ];

  // Sidebar del prestador
  const ProviderSidebar = () => {
    const sCard = { background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 14, fontFamily: font };
    const sHdr  = { padding: "14px 16px 0", display: "flex", alignItems: "center", gap: 8, marginBottom: 12 };
    const sAcc  = { width: 3, height: 13, background: "#E7A432", borderRadius: 2, flexShrink: 0 };
    const sTit  = { fontSize: 11, fontWeight: 700, color: "#163244", textTransform: "uppercase", letterSpacing: 1 };

    return (
      <div style={{ fontFamily: font }}>
        {/* Card prestador */}
        <div style={{ background: "#163244", borderRadius: 14, padding: "22px 18px", marginBottom: 14, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 85% 15%, rgba(231,164,50,0.15) 0%, transparent 55%)", pointerEvents: "none" }} />
          <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            {p.photo_url ? (
              <img src={p.photo_url} alt={p.display_name} style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", border: "3px solid #E7A432" }} />
            ) : (
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#E7A432", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800, color: "#163244" }}>
                {p.display_name?.[0]}
              </div>
            )}
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 15, marginBottom: 2 }}>{p.display_name}</div>
              <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 11, marginBottom: 6 }}>{p.legal_name}</div>
              <Badge status={p.status} />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={sCard}>
          <div style={sHdr}><div style={sAcc}/><span style={sTit}>Estadísticas</span></div>
          <div style={{ padding: "0 16px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#E7A432", lineHeight: 1 }}>⭐ {p.rating_avg || "—"}</div>
              <div style={{ fontSize: 10, color: "#7a93a4", marginTop: 3 }}>Calificación</div>
            </div>
            <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#163244", lineHeight: 1 }}>{p.jobs_completed || 0}</div>
              <div style={{ fontSize: 10, color: "#7a93a4", marginTop: 3 }}>Trabajos</div>
            </div>
            <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "12px 14px", textAlign: "center", gridColumn: "1 / -1" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#163244", lineHeight: 1 }}>
                {(p.services || []).filter(s => s.status === "approved").length}
                <span style={{ fontSize: 14, color: "#7a93a4" }}>/{(p.services || []).length}</span>
              </div>
              <div style={{ fontSize: 10, color: "#7a93a4", marginTop: 3 }}>Servicios aprobados</div>
            </div>
          </div>
        </div>

        {/* Verificaciones */}
        <div style={sCard}>
          <div style={sHdr}><div style={sAcc}/><span style={sTit}>Verificaciones</span></div>
          <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { label: "Identidad", ok: p.identity_verified, icon: "🪪" },
              { label: "Teléfono",  ok: p.phone_verified,    icon: "📱" },
            ].map(v => (
              <div key={v.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 9, background: v.ok ? "rgba(27,127,78,0.07)" : "rgba(192,57,43,0.07)", border: `1px solid ${v.ok ? "rgba(27,127,78,0.2)" : "rgba(192,57,43,0.2)"}` }}>
                <span style={{ fontSize: 16 }}>{v.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: v.ok ? "#1b7f4e" : "#c0392b", flex: 1 }}>{v.label}</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: v.ok ? "#1b7f4e" : "#c0392b" }}>{v.ok ? "✓" : "✕"}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Info */}
        <div style={sCard}>
          <div style={sHdr}><div style={sAcc}/><span style={sTit}>Información</span></div>
          <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              { label: "DNI",  value: p.dni  ? formatDNI(p.dni)   : "—" },
              { label: "CUIT", value: p.cuit ? formatCUIT(p.cuit) : "—" },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid rgba(0,0,0,0.05)", fontSize: 12 }}>
                <span style={{ color: "#7a93a4" }}>{label}</span>
                <span style={{ color: "#163244", fontWeight: 700 }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tip */}
        <div style={sCard}>
          <div style={{ padding: "14px 16px" }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: "rgba(231,164,50,0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#163244", marginBottom: 3 }}>Perfil completo = más trabajo</div>
            <div style={{ fontSize: 11, color: "#7a93a4", lineHeight: 1.55 }}>Los prestadores con foto y documentación al día reciben hasta un 40% más de solicitudes.</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", padding: "28px 24px 60px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 290px", gap: 20, alignItems: "start" }}>

          {/* Columna izquierda */}
          <div>
            {/* Header */}
            <div style={{ marginBottom: 18 }}>
              <h1 style={{ margin: "0 0 4px", fontSize: 21, fontWeight: 800, color: "#163244" }}>Mi perfil de prestador</h1>
              <p style={{ margin: 0, color: "#7a93a4", fontSize: 13 }}>Administrá tu información, servicios y documentación.</p>
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 4, marginBottom: 18, background: "#fff", borderRadius: 12, padding: 5, border: "1px solid rgba(0,0,0,0.07)", flexWrap: "wrap" }}>
              {TABS.map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                  flex: 1, minWidth: 80, padding: "8px 6px", borderRadius: 9, border: "none",
                  background: activeTab === t.id ? "#163244" : "transparent",
                  color: activeTab === t.id ? "#fff" : "#5a6e7f",
                  fontFamily: font, fontWeight: activeTab === t.id ? 700 : 500,
                  fontSize: 11, cursor: "pointer", transition: "all 0.15s",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                }}>
                  <span>{t.icon}</span><span>{t.label}</span>
                </button>
              ))}
            </div>

            {/* Contenido por tab */}
            {activeTab === "perfil"         && <PhotoSection currentPhoto={p.photo_url || ""} onUpdate={url => setProvider(prev => ({ ...prev, photo_url: url }))} />}
            {activeTab === "datos"          && <>
              <ProfileSection data={p} onUpdate={updated => setProvider(prev => ({ ...prev, ...updated }))} />
              <PersonalDataSection />
            </>}
            {activeTab === "servicios"      && <ServicesSection     services={p.services || []} />}
            {activeTab === "cobertura"      && <CoverageSection     coverage={p.coverage || p.coverage_areas || []} />}
            {activeTab === "disponibilidad" && <AvailabilitySection availability={p.availability || []} onUpdate={updated => setProvider(prev => ({ ...prev, availability: updated }))} />}
            {activeTab === "documentos"     && <DocumentsSection    services={p.services || []} />}
            {activeTab === "calificaciones" && <RatingsSection      providerId={p.id} />}
          </div>

          {/* Sidebar */}
          <ProviderSidebar />
        </div>
        {/* Guías de la Comunidad, punto 8: el camino para avisar, a la vista. */}
        <ReportarProblema />
      </div>

      <style>{`
        * { box-sizing: border-box; }
        input:focus, select:focus, textarea:focus { border-color: #163244 !important; box-shadow: 0 0 0 3px rgba(22,50,68,0.08); }
        input[type="time"]::-webkit-datetime-edit-ampm-field { display: none; }
        input[type="time"] { font-variant-numeric: tabular-nums; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
